import React, { useState, useEffect, useMemo } from 'react';
import { Plus, X, Save, Trash2, Scale, Settings2 } from 'lucide-react';

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const REAJUSTES = [
  { v: 0, label: '00 - NÃO INFORMADO' },
  { v: 5, label: '01 - 5%' },
  { v: 7, label: '02 - 7%' },
  { v: 8, label: '03 - 8%' },
  { v: 10, label: '04 - 10%' },
];

const mesesVazios = (ano) => MESES.map((m, i) => ({
  mes: i + 1, ano, salMinimo: 0, salRecebido: 0, reajuste: 0, salDireito: 0, indice: 1, difCorrigida: 0,
}));

const blankForm = () => ({
  data_calculo: new Date().toISOString().split('T')[0], tipo_calculo: 'Validação',
  tipo_contrato: '1 - CONTRATO POR PRAZO INDETERMINADO',
  cpf: '', nome: '', data_nascimento: '', ctps: '', serie: '', uf: '',
  empresa_cnpj: '', empresa_razao: '', data_admissao: '', data_demissao: '',
  convencao: 'DEFAULT - Convenção padrão', numero_controle: '', categoria: '01 - Assessoramento',
  ano: new Date().getFullYear(), meses: mesesVazios(new Date().getFullYear()), status: 'Em andamento',
});

const DiferencaSalarialModule = ({ darkMode }) => {
  const [lista, setLista] = useState([]);
  const [indices, setIndices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('lista'); // 'lista' | 'form' | 'indices'
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [novoIndice, setNovoIndice] = useState({ ano: new Date().getFullYear(), mes: 1, indice: 'GERAL', valor: 1 });

  const card = darkMode ? 'bg-[#161b27] border border-[#1e293b]' : 'bg-white border border-slate-200';
  const input = darkMode
    ? 'bg-[#1e2537] border border-[#2d3748] text-slate-200 placeholder-slate-500'
    : 'bg-slate-50 border border-slate-200 text-slate-800';
  const label = darkMode ? 'text-slate-400' : 'text-slate-500';
  const txt = darkMode ? 'text-slate-200' : 'text-slate-800';
  const sub = darkMode ? 'text-slate-500' : 'text-slate-400';
  const inputCls = `w-full p-2.5 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 ${input}`;
  const labelCls = `text-xs font-bold uppercase tracking-wider ${label}`;

  const carregar = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/diferencas-salariais`);
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar diferenças salariais:', e);
    } finally {
      setLoading(false);
    }
  };
  const carregarIndices = async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/indices-correcao`);
      const data = await res.json();
      setIndices(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erro ao carregar índices:', e);
    }
  };

  useEffect(() => { carregar(); carregarIndices(); }, []);

  const buscarIndice = (ano, mes) => {
    const r = indices.find(i => Number(i.ano) === Number(ano) && Number(i.mes) === Number(mes));
    return r ? Number(r.valor) : 1;
  };

  const recalcularMeses = (meses, ano) => {
    let salDireitoAnterior = null;
    return meses.map((m) => {
      const salMinimo = Number(m.salMinimo) || 0;
      const salRecebido = Number(m.salRecebido) || 0;
      const reajuste = Number(m.reajuste) || 0;
      let base = salDireitoAnterior === null ? salMinimo : salDireitoAnterior;
      const salDireito = Math.max(base * (1 + reajuste / 100), salMinimo);
      salDireitoAnterior = salDireito;
      const indice = m.indiceManual ? Number(m.indice) : buscarIndice(ano, m.mes);
      const difCorrigida = Math.max(salDireito - salRecebido, 0) * indice;
      return { ...m, salDireito, indice, difCorrigida };
    });
  };

  const calcular = () => {
    setForm(f => ({ ...f, meses: recalcularMeses(f.meses, f.ano) }));
  };

  const totalGeral = useMemo(() => form.meses.reduce((s, m) => s + (Number(m.difCorrigida) || 0), 0), [form.meses]);

  const novo = () => { setForm(blankForm()); setEditId(null); setView('form'); };

  const editar = (d) => {
    setForm({ ...blankForm(), ...d, meses: (d.meses && d.meses.length === 12) ? d.meses : mesesVazios(d.ano) });
    setEditId(d.id);
    setView('form');
  };

  const salvar = async () => {
    if (!form.nome) { alert('Informe o nome do trabalhador.'); return; }
    const payload = { ...form, total_geral: totalGeral };
    try {
      if (editId) {
        await fetch(`${apiBaseUrl}/api/diferencas-salariais/${editId}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      } else {
        await fetch(`${apiBaseUrl}/api/diferencas-salariais`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      }
      setView('lista');
      carregar();
    } catch (e) {
      console.error('Erro ao salvar diferença salarial:', e);
      alert('Erro ao salvar. Veja o console.');
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este cálculo?')) return;
    await fetch(`${apiBaseUrl}/api/diferencas-salariais/${id}`, { method: 'DELETE' });
    carregar();
  };

  const setMes = (idx, campo, valor) => {
    setForm(f => {
      const meses = f.meses.map((m, i) => i === idx ? { ...m, [campo]: valor, ...(campo === 'indice' ? { indiceManual: true } : {}) } : m);
      return { ...f, meses };
    });
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const trocarAno = (ano) => {
    setForm(f => ({ ...f, ano: Number(ano), meses: mesesVazios(Number(ano)) }));
  };

  const salvarIndice = async () => {
    if (!novoIndice.valor) { alert('Informe o valor do índice.'); return; }
    await fetch(`${apiBaseUrl}/api/indices-correcao`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(novoIndice),
    });
    carregarIndices();
  };
  const excluirIndice = async (id) => {
    await fetch(`${apiBaseUrl}/api/indices-correcao/${id}`, { method: 'DELETE' });
    carregarIndices();
  };

  // ── Tela: gerenciar índices ──
  if (view === 'indices') {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-black tracking-tight ${txt}`}>Índices de Correção</h2>
            <p className={`text-sm font-medium ${sub}`}>Cadastre os índices mensais usados no cálculo de diferença salarial.</p>
          </div>
          <button onClick={() => setView('lista')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${darkMode ? 'border-[#2d3748] text-slate-300 hover:bg-[#1e293b]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <X size={16} /> Voltar
          </button>
        </div>
        <div className={`${card} rounded-2xl p-6 shadow-sm grid grid-cols-1 md:grid-cols-5 gap-4 items-end`}>
          <div className="space-y-1"><label className={labelCls}>Ano</label>
            <input type="number" className={inputCls} value={novoIndice.ano} onChange={e => setNovoIndice(n => ({ ...n, ano: e.target.value }))} /></div>
          <div className="space-y-1"><label className={labelCls}>Mês</label>
            <select className={inputCls} value={novoIndice.mes} onChange={e => setNovoIndice(n => ({ ...n, mes: e.target.value }))}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select></div>
          <div className="space-y-1"><label className={labelCls}>Índice / Convenção</label>
            <input className={inputCls} value={novoIndice.indice} onChange={e => setNovoIndice(n => ({ ...n, indice: e.target.value }))} /></div>
          <div className="space-y-1"><label className={labelCls}>Valor (fator, ex: 1.0234)</label>
            <input type="number" step="0.0001" className={inputCls} value={novoIndice.valor} onChange={e => setNovoIndice(n => ({ ...n, valor: e.target.value }))} /></div>
          <button onClick={salvarIndice} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all">Salvar Índice</button>
        </div>
        <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
          {indices.length === 0 ? (
            <div className={`p-10 text-center text-sm ${sub}`}>Nenhum índice cadastrado ainda.</div>
          ) : (
            indices.map((i, idx) => (
              <div key={i.id} className={`flex items-center px-6 py-3 text-sm ${idx < indices.length - 1 ? `border-b ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}` : ''}`}>
                <span className={`w-20 font-bold ${txt}`}>{MESES[i.mes - 1]}/{i.ano}</span>
                <span className={`flex-1 ${sub}`}>{i.indice}</span>
                <span className={`font-bold mr-4 ${txt}`}>{i.valor}</span>
                <button onClick={() => excluirIndice(i.id)} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all"><Trash2 size={15} /></button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Tela: lista ──
  if (view === 'lista') {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className={`text-2xl font-black tracking-tight ${txt}`}>Diferença Salarial</h2>
            <p className={`text-sm font-medium ${sub}`}>Calcule diferenças salariais corrigidas mês a mês.</p>
          </div>
          <div className="flex gap-3">
            <button onClick={() => setView('indices')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${darkMode ? 'border-[#2d3748] text-slate-300 hover:bg-[#1e293b]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              <Settings2 size={16} /> Índices
            </button>
            <button onClick={novo} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md transition-all">
              <Plus size={18} /> Novo Cálculo
            </button>
          </div>
        </div>

        <div className={`${card} rounded-2xl shadow-sm overflow-hidden`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${sub}`}>Carregando...</div>
          ) : lista.length === 0 ? (
            <div className={`p-10 text-center text-sm ${sub}`}>
              <Scale size={36} className="mx-auto mb-3 opacity-30" />
              <p>Nenhum cálculo encontrado.</p>
              <p className="text-xs mt-1">Clique em "Novo Cálculo" para começar.</p>
            </div>
          ) : (
            lista.map((d, i) => (
              <div key={d.id} onClick={() => editar(d)}
                className={`flex items-center px-6 py-4 cursor-pointer ${i < lista.length - 1 ? `border-b ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}` : ''} transition-colors ${darkMode ? 'hover:bg-[#1e293b]' : 'hover:bg-slate-50'}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mr-4 bg-blue-500/10">
                  <Scale size={18} className="text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${txt}`}>{d.nome} — {d.empresa_razao || 'sem empresa'}</p>
                  <p className={`text-xs mt-0.5 ${sub}`}>Ano {d.ano} · {d.status}</p>
                </div>
                <p className={`text-sm font-black mr-4 ${txt}`}>{fmt(d.total_geral)}</p>
                <button onClick={(e) => { e.stopPropagation(); excluir(d.id); }}
                  className={`p-2 rounded-lg transition-all ${darkMode ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10' : 'text-slate-300 hover:text-red-500 hover:bg-red-50'}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Tela: form ──
  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className={`${card} rounded-2xl p-6 shadow-sm flex items-center justify-between`}>
        <div>
          <h2 className={`text-2xl font-black tracking-tight ${txt}`}>{editId ? 'Editar Cálculo' : 'Novo Cálculo de Diferença Salarial'}</h2>
          <p className={`text-sm font-medium ${sub}`}>Preencha os dados, gere a tabela mensal e calcule.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setView('lista')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold border transition-all ${darkMode ? 'border-[#2d3748] text-slate-300 hover:bg-[#1e293b]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <X size={16} /> Cancelar
          </button>
          <button onClick={salvar} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-md transition-all">
            <Save size={16} /> Salvar Dados
          </button>
        </div>
      </div>

      <div className={`${card} rounded-2xl p-6 shadow-sm space-y-4`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1"><label className={labelCls}>Data do Cálculo</label>
            <input type="date" className={inputCls} value={form.data_calculo} onChange={e => set('data_calculo', e.target.value)} /></div>
          <div className="space-y-1"><label className={labelCls}>Tipo de Cálculo</label>
            <div className="flex gap-4 pt-2">
              {['Validação', 'Cálculo'].map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={form.tipo_calculo === t} onChange={() => set('tipo_calculo', t)} /> <span className={`text-sm ${txt}`}>{t}</span>
                </label>
              ))}
            </div></div>
          <div className="space-y-1"><label className={labelCls}>Tipo de Contrato</label>
            <select className={inputCls} value={form.tipo_contrato} onChange={e => set('tipo_contrato', e.target.value)}>
              <option>1 - CONTRATO POR PRAZO INDETERMINADO</option>
              <option>2 - CONTRATO POR PRAZO DETERMINADO</option>
              <option>3 - CONTRATO DE EXPERIÊNCIA</option>
            </select></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
          <div className="space-y-3">
            <p className={`text-xs font-black uppercase ${label}`}>Identificação do Trabalhador</p>
            <input className={inputCls} placeholder="CPF" value={form.cpf} onChange={e => set('cpf', e.target.value)} />
            <input className={inputCls} placeholder="Nome" value={form.nome} onChange={e => set('nome', e.target.value)} />
            <input type="date" className={inputCls} value={form.data_nascimento} onChange={e => set('data_nascimento', e.target.value)} />
            <div className="grid grid-cols-3 gap-2">
              <input className={inputCls} placeholder="C.T.P.S" value={form.ctps} onChange={e => set('ctps', e.target.value)} />
              <input className={inputCls} placeholder="Série" value={form.serie} onChange={e => set('serie', e.target.value)} />
              <input className={inputCls} placeholder="UF" value={form.uf} onChange={e => set('uf', e.target.value)} />
            </div>
          </div>
          <div className="space-y-3">
            <p className={`text-xs font-black uppercase ${label}`}>Empresa</p>
            <input className={inputCls} placeholder="CNPJ / CPF / CEI" value={form.empresa_cnpj} onChange={e => set('empresa_cnpj', e.target.value)} />
            <input className={inputCls} placeholder="Razão Social / Nome" value={form.empresa_razao} onChange={e => set('empresa_razao', e.target.value)} />
            <div className="space-y-1"><label className={labelCls}>Convenção</label>
              <input className={inputCls} value={form.convencao} onChange={e => set('convencao', e.target.value)} /></div>
          </div>
          <div className="space-y-3">
            <p className={`text-xs font-black uppercase ${label}`}>Dados do Contrato</p>
            <div className="grid grid-cols-2 gap-2">
              <input type="date" className={inputCls} value={form.data_admissao} onChange={e => set('data_admissao', e.target.value)} />
              <input type="date" className={inputCls} value={form.data_demissao} onChange={e => set('data_demissao', e.target.value)} />
            </div>
            <input className={inputCls} placeholder="Nº de Controle" value={form.numero_controle} onChange={e => set('numero_controle', e.target.value)} />
            <select className={inputCls} value={form.categoria} onChange={e => set('categoria', e.target.value)}>
              <option>01 - Assessoramento</option>
              <option>02 - Direção</option>
              <option>03 - Execução</option>
            </select>
            <button onClick={calcular} className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-all">Calcular</button>
          </div>
        </div>
      </div>

      <div className={`${card} rounded-2xl p-6 shadow-sm`}>
        <div className="flex items-center justify-between mb-4">
          <p className={`text-sm font-black ${txt}`}>Ano</p>
          <select className={`${inputCls} w-32`} value={form.ano} onChange={e => trocarAno(e.target.value)}>
            {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 3 + i).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${label} text-xs uppercase`}>
                <th className="py-2 pr-3">Mês | Ano</th>
                <th className="py-2 pr-3">Sal. Mínimo</th>
                <th className="py-2 pr-3">Sal. Recebido</th>
                <th className="py-2 pr-3">Reajuste Salarial</th>
                <th className="py-2 pr-3">Sal. de Direito</th>
                <th className="py-2 pr-3">Índice de Corr.</th>
                <th className="py-2 pr-3">Dif. Corrigida</th>
              </tr>
            </thead>
            <tbody>
              {form.meses.map((m, idx) => (
                <tr key={idx} className={`border-t ${darkMode ? 'border-[#1e293b]' : 'border-slate-100'}`}>
                  <td className={`py-1.5 pr-3 font-bold ${txt}`}>{MESES[idx]} - {String(form.ano).slice(-2)}</td>
                  <td className="py-1.5 pr-3"><input type="number" className={inputCls} value={m.salMinimo} onChange={e => setMes(idx, 'salMinimo', e.target.value)} /></td>
                  <td className="py-1.5 pr-3"><input type="number" className={inputCls} value={m.salRecebido} onChange={e => setMes(idx, 'salRecebido', e.target.value)} /></td>
                  <td className="py-1.5 pr-3">
                    <select className={inputCls} value={m.reajuste} onChange={e => setMes(idx, 'reajuste', e.target.value)}>
                      {REAJUSTES.map(r => <option key={r.label} value={r.v}>{r.label}</option>)}
                    </select>
                  </td>
                  <td className={`py-1.5 pr-3 font-bold ${txt}`}>{fmt(m.salDireito)}</td>
                  <td className="py-1.5 pr-3"><input type="number" step="0.0001" className={inputCls} value={m.indice} onChange={e => setMes(idx, 'indice', e.target.value)} /></td>
                  <td className="py-1.5 pr-3 font-black text-emerald-500">{fmt(m.difCorrigida)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end pt-4">
          <p className={`text-base font-black ${txt}`}>Total Geral: {fmt(totalGeral)}</p>
        </div>
      </div>
    </div>
  );
};

export default DiferencaSalarialModule;
